import React from 'react';
import { useNavigate, useParams } from 'react-router';

import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { links } from '@core/router';
import {
    ActivityType,
    CompositePointSourceType
} from '@api/FitnessActivityClient';

import {
    PointDefinitionForm,
    PointDefinitionFormData
} from '../point-definition-form';
import { useEditPointDefinitionStore } from './store';

export const EditPointDefinitionForm = () => {
    const { communityId, definitionId } = useParams();
    const navigate = useNavigate();

    const communityIdNum = Number(communityId);
    const definitionIdNum = Number(definitionId);

    const { getDefinition, updateRequest, onSubmit } =
        useEditPointDefinitionStore(communityIdNum, definitionIdNum);

    if (isNaN(communityIdNum) || isNaN(definitionIdNum)) return null;

    const definition = getDefinition.$;
    const initialData = React.useMemo<
        PointDefinitionFormData | undefined
    >(() => {
        if (!definition) return undefined;
        return {
            pointTypeKey: definition.pointTypeKey,
            label: definition.label ?? '',
            symbolSvg: definition.symbolSvg || '',
            enabled: definition.enabled,
            dayFrom: definition.dayFrom ?? '',
            rules: definition.sourceRules.map((rule) => ({
                sourceType: rule.sourceType as CompositePointSourceType | '',
                sourceKey: rule.sourceKey ?? '',
                workoutCategory: rule.workoutCategory ?? '',
                workoutType: rule.workoutType ?? '',
                expression: rule.expression,
                priority: String(rule.priority),
                enabled: rule.enabled
            }))
        };
    }, [definition]);

    const handleSubmit = async (formData: PointDefinitionFormData) => {
        await onSubmit({
            pointTypeKey: formData.pointTypeKey,
            label: formData.label,
            symbolSvg: formData.symbolSvg
                ? btoa(formData.symbolSvg)
                : undefined,
            enabled: formData.enabled,
            dayFrom: formData.dayFrom || undefined,
            sourceRules: formData.rules.map((rule) => ({
                sourceType: rule.sourceType as CompositePointSourceType,
                sourceKey: rule.sourceKey || undefined,
                workoutCategory:
                    rule.sourceType ===
                        CompositePointSourceType.ACTIVITY_POINTS &&
                    rule.sourceKey === ActivityType.WORKOUT
                        ? rule.workoutCategory || undefined
                        : undefined,
                workoutType:
                    rule.sourceType ===
                        CompositePointSourceType.ACTIVITY_POINTS &&
                    rule.sourceKey === ActivityType.WORKOUT
                        ? rule.workoutType || undefined
                        : undefined,
                expression: rule.expression,
                priority: parseInt(rule.priority, 10),
                enabled: rule.enabled
            }))
        });
    };

    return getDefinition.match({
        loading: () => (
            <div className="flex justify-center p-8">
                <Spinner />
            </div>
        ),
        error: () => (
            <Alert variant="destructive">
                <AlertDescription>
                    Failed to load point definition. Please try again.
                </AlertDescription>
            </Alert>
        ),
        fetched: () => {
            if (!initialData) {
                return (
                    <Alert variant="destructive">
                        <AlertDescription>
                            Point definition not found.
                        </AlertDescription>
                    </Alert>
                );
            }

            return (
                <PointDefinitionForm
                    title="Edit Point Definition"
                    submitLabel="Save"
                    submittingLabel="Saving..."
                    initialData={initialData}
                    isSubmitting={updateRequest.loading}
                    onCancel={() =>
                        navigate(links.communityPointDefinitions(communityId))
                    }
                    onSubmit={handleSubmit}
                />
            );
        }
    });
};

export default EditPointDefinitionForm;
